import { injectable, multiInject, optional } from 'inversify';
import { v4 as uuidv4 } from 'uuid';
import { EventStreamListener, EventStreamListenerSymbol } from './event-stream-listener';
import { Event } from './event';

@injectable()
export class EventStream {
  private functionListeners: {
    [name: string]: {
      [id: string]: any
    }
  } = {};

  private listenerMap: {
    [name: string]: EventStreamListener[] | undefined
  } = {};
  // listeners which listen to all of the events
  private allListeners: EventStreamListener[] = [];

  constructor(
    @optional()
    @multiInject(EventStreamListenerSymbol)
    listeners: EventStreamListener[]) {

    this.initializeListeners(listeners);
  }

  public dispatch(event: Event|Event[]): void {
    const events: Event[] = !Array.isArray(event) ? [event] : event;
    events.forEach((e) => this.dispatchOne(e));
  }

  public listen<T>(name: string, callback: (payload: T) => void): string {
    if (!this.functionListeners[name]) {
      this.functionListeners[name] = {};
    }

    const id: string = uuidv4();
    this.functionListeners[name][id] = callback;

    return id;
  }

  public removeListener(name: string, id: string): void {
    if (this.functionListeners[name]) {
      // @see: https://stackoverflow.com/questions/53753276/
      // javascript-spread-and-rest-syntax-to-remove-specific-property-from-object
      const { [id]: _, ...newListeners } = this.functionListeners[name];
      this.functionListeners[name] = newListeners;
    }
  }

  private dispatchOne(event: Event): void {

    if ((event.payload as any).__ob__) {
      throw new Error(`Event contains vue observer ${JSON.stringify(event)}`);
    }


    for (const listener of this.allListeners) {
      listener.handle(event);
    }

    const name: string = event.name;
    const listeners = this.listenerMap[name];
    if (listeners) {
      for (const listener of listeners) {
        listener.handle(event);
      }
    }

    if (this.functionListeners[name]) {
      Object.values(this.functionListeners[name]).forEach((callback: any) => {
        callback(event.payload);
      });
    }
  }

  private initializeListeners(listeners: EventStreamListener[]) {
    for (const listener of listeners) {
      if (!listener.events) {
        continue;
      }

      if (listener.events === 'all') {
        this.allListeners.push(listener);
        continue;
      }

      for (const event of listener.events) {
        const eventListeners = this.getOrInsertListenersForName(event);
        eventListeners.push(listener);
      }
    }

    // console.log('INJECTED listeners', this.listenerMap, this.allListeners);
  }

  private getOrInsertListenersForName(name: string) {
    let list = this.listenerMap[name];
    if (!list) {
      list = [];
      this.listenerMap[name] = list;
    }

    return list;
  }
}

export const EventStreamSymbol = Symbol.for('EventStream');
